import { useTranslation } from 'react-i18next';

import { CalculatedWorkDemand } from 'components/types';

import { getLocalizedMonthNames } from 'utils/date';
import { displayCurrency, displayNumber } from 'utils/misc';

import {
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableCell
} from '@mui/material';

import {
  NumericTableCell,
  TableHeaderRow,
  ZebraStripedTableRow
} from 'components/shared/Table';

import {
  getPeriodTotalsForWorkDemandTotals,
  sumPeriodTotalsForPeriods
} from '../../util';

type Props = {
  isShowHours: boolean;
  totalWorkDemand: CalculatedWorkDemand | null;
};

const monthNames = getLocalizedMonthNames();

const WorkDemandTotalsTable = ({ isShowHours, totalWorkDemand }: Props) => {
  const { t } = useTranslation('common');

  const renderTotals = () => {
    if (!totalWorkDemand) {
      return null;
    }

    const totals = getPeriodTotalsForWorkDemandTotals(totalWorkDemand.rows);
    const yearTotals = sumPeriodTotalsForPeriods(totals);

    return (
      <>
        {totals.map(({ number: periodNr, hours, revenue }) => (
          <NumericTableCell key={`totals_${periodNr}`}>
            <strong>
              {isShowHours ? displayNumber(hours) : displayCurrency(revenue)}
            </strong>
          </NumericTableCell>
        ))}
        <NumericTableCell style={{ maxWidth: '120px' }}>
          <strong>
            {isShowHours
              ? displayNumber(yearTotals.hours)
              : displayCurrency(yearTotals.revenue)}
          </strong>
        </NumericTableCell>
      </>
    );
  };

  return (
    <TableContainer component={Paper}>
      <Table stickyHeader size="small">
        <TableHead>
          <TableHeaderRow>
            <TableCell>{t('description')}</TableCell>
            {monthNames.map((monthName) => (
              <NumericTableCell width={120} key={monthName}>
                {monthName}
              </NumericTableCell>
            ))}
            <NumericTableCell width={120}>{t('yearTotal')}</NumericTableCell>
          </TableHeaderRow>
        </TableHead>
        <TableBody>
          {totalWorkDemand && (
            <>
              {totalWorkDemand.rows.map((row) => {
                const totals = sumPeriodTotalsForPeriods(row.periods);

                return (
                  <ZebraStripedTableRow key={`calc_${row.description}`}>
                    <TableCell>{row.description}</TableCell>
                    {row.periods.map((period) => (
                      <NumericTableCell
                        width={120}
                        key={`calc_${row.description}_${period.number}`}
                      >
                        {isShowHours
                          ? displayNumber(period.hours)
                          : displayCurrency(period.revenue)}
                      </NumericTableCell>
                    ))}
                    <NumericTableCell width={120}>
                      {isShowHours
                        ? displayNumber(totals.hours)
                        : displayCurrency(totals.revenue)}
                    </NumericTableCell>
                  </ZebraStripedTableRow>
                );
              })}
              <ZebraStripedTableRow>
                <TableCell>
                  <strong>{t('totals')}</strong>
                </TableCell>
                {renderTotals()}
              </ZebraStripedTableRow>
            </>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default WorkDemandTotalsTable;
